import type { Transaction } from "@rocicorp/zero";
import { zql } from "../zql";
import { Crews } from "./crews.handlers";

export namespace TicketAssignments {
	export async function getTicketAssignmentById(tx: Transaction, ticketAssignmentId: string) {
		const ticketAssignment = await tx.run(
			zql.ticketAssignments.where("id", "=", ticketAssignmentId).one(),
		);
		return ticketAssignment;
	}

	/**
	 * The ranking that appends a new assignment below everything currently on the crew: one step
	 * past the highest ranking in place, or 0 on an empty column. One step is enough to be
	 * unambiguous — the re-spacing pass that follows every write keeps neighbours 100 apart — and
	 * because each mutation sees the writes of the one before it, a burst of inserts lands in call
	 * order instead of tying.
	 *
	 * @param tx - The Zero transaction the mutator runs in.
	 * @param crewId - The crew whose column the assignment joins.
	 * @returns The ranking to insert with.
	 */
	export async function getNextRanking(tx: Transaction, crewId: string) {
		const last = await tx.run(
			zql.ticketAssignments.where("crewId", "=", crewId).orderBy("ranking", "desc").one(),
		);
		if (!last) return 0;
		return last.ranking + 1;
	}

	export async function getReferenceId(
		tx: Transaction,
		crewId: string,
		projectId: string,
		newReferenceId: string,
	) {
		const currentReference = await tx.run(
			zql.ticketAssignmentReferences
				.where("projectId", "=", projectId)
				.where("crewId", "=", crewId)
				.one(),
		);
		if (currentReference?.id) {
			return currentReference.id;
		} else {
			const crew = await Crews.getCrew(tx, crewId);
			if (!crew?.planId) return null;
			const plan = await tx.run(zql.plans.where("id", "=", crew.planId).one());
			if (!plan?.orgId) return null;
			const isExternalPlan = plan?.orgId !== crew.orgId;
			await tx.mutate.ticketAssignmentReferences.insert({
				crewId,
				projectId,
				isExternalPlan,
				isInternalPlan: !isExternalPlan,
				orgId: plan?.orgId,
				calculationType: "hourly",
				id: newReferenceId,
			});
			return newReferenceId;
		}
	}

	export async function getTicketAssignmentsByReferenceId(tx: Transaction, referenceId: string) {
		const ticketAssignments = await tx.run(
			zql.ticketAssignments.where("referenceId", "=", referenceId),
		);
		return ticketAssignments;
	}

	export async function cleanReference(tx: Transaction, referenceId: string) {
		const ticketAssignments = await getTicketAssignmentsByReferenceId(tx, referenceId);
		if (ticketAssignments.length > 0) return tx;
		return await tx.mutate.ticketAssignmentReferences.delete({ id: referenceId });
	}
}
